"use client"

// src/form/index.ts
import React3, { useMemo as useMemo3, useState } from "react";

// src/form/component.tsx
import { Button, Checkbox, DatePicker, Dialog, Error as Error3, Input, Select, useDialogContext as useDialogContext2 } from "@lb-lib/ui";
import React2, { useEffect as useEffect2, useMemo as useMemo2 } from "react";
import * as Yup2 from "yup";

// src/useForm.tsx
import { Error as Error2, useDialogContext } from "@lb-lib/ui";
import equal from "fast-deep-equal";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef } from "react";
import * as Yup from "yup";
var useForm = (opts) => {
  let dialog = useDialogContext();
  let schema = useMemo(() => opts.schema(Yup), opts.schemaDependencies || []);
  let formik = useFormik({
    ...opts,
    validationSchema: schema
  });
  let RenderError = ({
    field
  }) => {
    if (!formik.errors[field] || !formik.touched[field])
      return null;
    let reason = formik.errors[field];
    return /* @__PURE__ */ React.createElement(Error2, { style: {
      marginTop: 6
    }, size: 12 }, reason);
  };
  let prevValues = useRef(void 0);
  useEffect(() => {
    if (!opts.updateInitialValues)
      return;
    if (!equal(prevValues.current, opts.initialValues)) {
      formik.setValues(opts.initialValues);
      prevValues.current = opts.initialValues;
    }
  }, [opts.initialValues, opts.updateInitialValues]);
  return {
    ...formik,
    // Prevent formik from showing errors when dialog is closing
    errors: dialog?.isOpen === false ? {} : formik.errors,
    RenderError
  };
};

// src/form/component.tsx
var RenderFormField = ({
  field,
  form: form2,
  size,
  input
}) => {
  if (field.isVisible && !field.isVisible({
    value: form2.values,
    input
  }))
    return null;
  let disabled = field.isDisabled && field.isDisabled({
    value: form2.values,
    input
  });
  if (field.type == "textfield") {
    return /* @__PURE__ */ React2.createElement("div", null, /* @__PURE__ */ React2.createElement(Input, { as: field.as, type: field.kind, label: field.label, size: field.size ?? size, placeholder: field.placeholder, description: field.description, error: form2.touched[field.key] && form2.errors[field.key], minRows: field.minRows, disabled, ...form2.getFieldProps(field.key) }));
  }
  if (field.type == "select") {
    let items = field.getItems();
    return /* @__PURE__ */ React2.createElement(Select, { label: field.label, placeholder: field.placeholder, description: field.description, error: form2.touched[field.key] && form2.errors[field.key], items, value: form2.values[field.key], onChange: (v) => form2.setFieldValue(field.key, v), disabled });
  }
  if (field.type == "checkbox") {
    return /* @__PURE__ */ React2.createElement(Checkbox, { label: field.label, description: field.description, checked: form2.values[field.key], onCheckedChange: (v) => form2.setFieldValue(field.key, v), disabled });
  }
  if (field.type == "date") {
    return /* @__PURE__ */ React2.createElement(DatePicker, { type: field.kind, label: field.label, placeholder: field.placeholder, description: field.description, value: form2.values[field.key], resettable: field.resettable, onChange: (v) => form2.setFieldValue(field.key, v), disabled });
  }
  if (field.type == "group") {
    return /* @__PURE__ */ React2.createElement("div", { style: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      gap: 15
    } }, field.fields.map((f, i) => /* @__PURE__ */ React2.createElement("div", { key: "key" in f ? f.key : i }, /* @__PURE__ */ React2.createElement(RenderFormField, { field: f, form: form2, size, input }))));
  }
};
var FormComponent = ({
  initialData: parentInitialData,
  updateInitialValues,
  state: getState,
  hasCancelButton,
  submitButton,
  onSuccess,
  fields,
  styles,
  input,
  size
}) => {
  let dialog = useDialogContext2();
  let state = getState(input, {
    getValues: () => form2.values,
    setValues: (values) => form2.setValues(values)
  });
  let initialData = useMemo2(() => ({
    ...parentInitialData,
    ...state.initialData
  }), [parentInitialData, state.initialData]);
  let schema = useMemo2(() => {
    let sch = {};
    fields.forEach((f) => {
      if ("validation" in f && f.validation)
        sch[f.key] = f.validation(Yup2);
    });
    return Yup2.object().shape(sch);
  }, []);
  let form2 = useForm({
    initialValues: initialData,
    onSubmit: async (v) => {
      await state.mutate(v);
    },
    schema: () => schema,
    updateInitialValues
  });
  useEffect2(() => {
    if (state.isSuccess) {
      onSuccess?.({
        values: form2.values,
        result: state.result
      });
      if (dialog?.autoCloseOnSubmit !== false) {
        setTimeout(() => dialog?.onOpenChange(false), 500);
      }
    }
  }, [state.isSuccess]);
  return /* @__PURE__ */ React2.createElement("form", { onSubmit: form2.handleSubmit, style: {
    display: "flex",
    flexDirection: "column",
    gap: 15,
    ...styles
  } }, fields.map((f, i) => {
    return /* @__PURE__ */ React2.createElement(RenderFormField, { key: "key" in f ? f.key : i, field: f, form: form2, size, input });
  }), /* @__PURE__ */ React2.createElement("div", null, dialog ? /* @__PURE__ */ React2.createElement(Dialog.Actions, null, hasCancelButton !== false && /* @__PURE__ */ React2.createElement(Button, { type: "button", variant: "solid", size: submitButton.size, onClick: () => {
    if (state.isLoading || state.isSuccess)
      return;
    dialog.onOpenChange(false);
  } }, "Cancel"), /* @__PURE__ */ React2.createElement(Button, { type: "submit", loading: state.isLoading, success: state.isSuccess, variant: submitButton.variant, color: submitButton.color, size: submitButton.size }, submitButton.children)) : /* @__PURE__ */ React2.createElement(Button, { type: "submit", loading: state.isLoading, success: state.isSuccess, fullWidth: submitButton.fullWidth, variant: submitButton.variant, color: submitButton.color, size: submitButton.size }, submitButton.children)), state.error && /* @__PURE__ */ React2.createElement(Error3, { size: 12 }, state.error.data?.message || state.error.message));
};

// src/form/index.ts
var form = (opts) => ({
  initialData,
  onSuccess,
  hasCancelButton,
  ...customInput
}) => {
  let fullInitialData = useMemo3(() => ({
    ...opts.initialData,
    ...initialData
  }), [initialData]);
  return React3.createElement(FormComponent, {
    ...opts,
    onSuccess,
    hasCancelButton,
    input: customInput,
    initialData: fullInitialData
  });
};
var formHookIndex = 0;
var useFormComponent = (opts) => {
  return useState(() => form({
    ...opts(),
    name: `hook-form-${formHookIndex++}`
  }))[0];
};

// src/lib/atom.ts
import { useLayoutEffect, useState as useState2 } from "react";

// src/lib/listeners.ts
var createListeners = () => {
  let listeners = /* @__PURE__ */ new Set();
  let register = (listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  let notify = () => {
    for (let listener of listeners)
      listener();
  };
  let dispose = () => {
    listeners.clear();
  };
  return {
    register,
    notify,
    dispose
  };
};

// src/lib/atom.ts
var atom = (initialValue) => {
  let value = initialValue;
  let listeners = createListeners();
  let set = (newValue) => {
    if (typeof newValue === "function") {
      value = newValue(value);
    } else {
      value = newValue;
    }
    listeners.notify();
  };
  let get = () => value;
  let subscribe = (listener) => {
    let cb = () => listener(get());
    return listeners.register(cb);
  };
  return {
    set,
    get,
    subscribe
  };
};
var useAtom = (a) => {
  let [_, setState] = useState2({});
  useLayoutEffect(() => a.subscribe(() => {
    setState({});
  }), [a]);
  return a.get();
};

// src/lib/emitter.ts
var createStateEmitter = () => {
  let currentIndex = 0;
  let listeners = /* @__PURE__ */ new Map();
  let resources = /* @__PURE__ */ new Map();
  let register = (resourceIds, listener) => {
    let index = currentIndex++;
    listeners.set(index, listener);
    for (let resourceId of resourceIds) {
      let set = resources.get(resourceId);
      if (!set) {
        set = /* @__PURE__ */ new Set();
        resources.set(resourceId, set);
      }
      set.add(index);
    }
    return () => {
      for (let resourceId of resourceIds) {
        let set = resources.get(resourceId);
        if (set) {
          set.delete(index);
          if (set.size == 0)
            resources.delete(resourceId);
        }
      }
    };
  };
  let notify = (resourceIds) => {
    let listenersToNotify = /* @__PURE__ */ new Set();
    for (let resourceId of resourceIds) {
      let set = resources.get(resourceId);
      if (set) {
        for (let index of set)
          listenersToNotify.add(index);
      }
    }
    for (let index of listenersToNotify) {
      let listener = listeners.get(index);
      if (listener)
        listener();
    }
    return {
      didNotify: listenersToNotify.size > 0
    };
  };
  return {
    register,
    notify
  };
};

// src/lib/onFocus.ts
var onFocus = (handler) => {
  let listener = () => {
    if (document.hasFocus())
      handler();
  };
  window.addEventListener("focus", listener);
  return () => {
    window.removeEventListener("focus", listener);
  };
};

// src/loader/index.ts
import { canonicalize } from "@lb-lib/canonicalize";
import { ServiceError, isServiceError, serviceUnavailableError } from "@lb-lib/error";
import { memo } from "@lb-lib/memo";
import { useEffect as useEffect3, useLayoutEffect as useLayoutEffect2, useMemo as useMemo4, useRef as useRef2, useState as useState3 } from "react";

// src/loader/observable.ts
var Observable = class {
  valueRef;
  listeners = /* @__PURE__ */ new Set();
  constructor(initialValue) {
    this.valueRef = {
      current: initialValue
    };
  }
  get value() {
    return this.valueRef.current;
  }
  next(value) {
    this.valueRef.current = value;
    for (let listener of this.listeners) {
      listener(value);
    }
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  subscribeOnce(listener) {
    let unsubscribe = this.subscribe((value) => {
      listener(value);
      unsubscribe();
    });
  }
  hasSubscribers() {
    return this.listeners.size > 0;
  }
};

// src/loader/index.ts
var allStates = /* @__PURE__ */ new Map();
var createLoader = (opts) => {
  let refetchParents = () => {
    for (let parent of opts.parents ?? []) {
      parent.$$refetchAll$$();
    }
  };
  let states = allStates.get(opts.name);
  if (!states) {
    states = /* @__PURE__ */ new Map();
    allStates.set(opts.name, states);
  }
  let ensureState = memo((input) => {
    let hash = opts.hash ? opts.hash(input) : canonicalize(input);
    let state = states.get(hash);
    if (!state) {
      state = {
        state: new Observable({
          input,
          output: null,
          error: null
        }),
        lastFetchedAt: 0,
        active: false
      };
      states.set(hash, state);
    }
    return state;
  });
  let getMutators = memo(({
    current,
    refetch
  }) => {
    return Object.fromEntries(Object.entries(opts.mutators ?? {}).map(([key, mutator]) => {
      return [key, async (data) => {
        try {
          let res = await mutator(data, {
            input: current.state.value.input,
            output: current.state.value.output,
            setOutput: (output) => {
              current.state.next({
                ...current.state.value,
                output
              });
            }
          });
          refetch();
          refetchParents();
          return res;
        } catch (error) {
          if (isServiceError(error)) {
            throw error;
          } else {
            throw new ServiceError(serviceUnavailableError({
              message: "Unable to perform request"
            }));
          }
        }
      }];
    }));
  });
  let getInstanceFromCurrent = memo((current) => {
    let input = current.state.value.input;
    let fetchInner = async (input2, fetchOpts) => {
      if (current.active) {
        if (fetchOpts?.force)
          await new Promise((r) => current.state.subscribeOnce(r));
        else
          return;
      }
      current.active = true;
      console.log(`[${opts.name}] Fetching`, {
        input: input2
      });
      try {
        let output = await opts.fetch(input2);
        current.state.next({
          input: input2,
          output,
          error: null
        });
        opts.onSuccess?.(output);
      } catch (error) {
        console.error(`[${opts.name}] Fetch error`, error);
        if (isServiceError(error)) {
          current.state.next({
            input: input2,
            output: current.state.value?.output ?? null,
            error
          });
          opts.onError?.(error);
        } else {
          console.warn(`[${opts.name}] Fetch error`, error);
          let err = new ServiceError(serviceUnavailableError({
            message: "Unable to perform request"
          }));
          current.state.next({
            input: input2,
            output: current.state.value?.output ?? null,
            error: err
          });
          opts.onError?.(err);
        }
      } finally {
        current.active = false;
        current.lastFetchedAt = Date.now();
      }
    };
    let fetch = async (input2, fetchOpts) => {
      if (!fetchOpts?.force && Date.now() - current.lastFetchedAt < 1e4)
        return Promise.resolve();
      await fetchInner(input2, fetchOpts);
    };
    if (current.active || Date.now() - current.lastFetchedAt > 6e4)
      fetch(input);
    return {
      fullState: current,
      state: current.state,
      mutators: getMutators({
        current,
        refetch: () => {
          fetch(input, {
            force: true
          });
        }
      }),
      fetch: ({
        force = false
      } = {}) => fetch(input, {
        force
      })
    };
  });
  let getInstance = memo((input) => {
    let current = ensureState(input);
    return getInstanceFromCurrent(current);
  });
  let refetchAll = () => {
    if (document.hasFocus()) {
      for (let state of states.values()) {
        getInstance(state.state.value.input).fetch({
          force: true
        });
      }
    }
  };
  if (opts.polling) {
    setInterval(refetchAll, opts.polling.interval);
    onFocus(refetchAll);
  }
  let fetchManual = async (input) => {
    let inst = getInstance(input);
    inst.fetch();
    return inst.state;
  };
  let waitForBase = (state) => {
    return new Promise((resolve, reject) => {
      console.log("waiting for", state);
      if (state.value?.output) {
        resolve(state.value.output);
      } else if (state.value?.error) {
        reject(state.value.error);
      } else {
        let unsubscribe = state.subscribe((s) => {
          console.log("waiting for 2", s);
          if (s.output) {
            unsubscribe();
            resolve(s.output);
          } else if (s.error) {
            unsubscribe();
            reject(s.error);
          }
        });
      }
    });
  };
  let waitFor = (input) => waitForBase(ensureState(input).state);
  let fetchAndReturn = async (input) => waitForBase(await fetchManual(input));
  let use = (input) => {
    let subStateRefs = useRef2([]);
    let currentInstance = useMemo4(() => input === null ? null : getInstance(input), [input]);
    let [state, setState] = useState3(() => currentInstance?.state.value);
    useEffect3(() => {
      if (!currentInstance)
        return;
      setState(currentInstance.state.value);
      let unsubscribe = currentInstance.state.subscribe(() => {
        setState(currentInstance.state.value);
      });
      return () => unsubscribe();
    }, [currentInstance, currentInstance?.state]);
    useEffect3(() => {
      if (!currentInstance)
        return;
      currentInstance.fetch();
    }, [currentInstance]);
    useEffect3(() => {
      if (input)
        subStateRefs.current.forEach((r) => r.current(input));
    }, [input]);
    return {
      input: state?.input,
      data: state?.output,
      error: state?.error,
      isLoading: !state || !state.output && !state.error,
      mutators: currentInstance?.mutators ?? {},
      use: () => {
        let [input2, setInput] = useState3(currentInstance?.state.value.input ?? null);
        useLayoutEffect2(() => {
          subStateRefs.current.push({
            current: setInput
          });
          return () => {
            subStateRefs.current = subStateRefs.current.filter((r) => r.current !== setInput);
          };
        }, [setInput]);
        return use(input2);
      }
    };
  };
  return {
    refetchAll,
    $$refetchAll$$: refetchAll,
    fetch: fetchManual,
    waitFor,
    fetchAndReturn,
    getState: (input) => {
      let current = ensureState(input);
      return current.state.value;
    },
    subscribe: (input, callback) => {
      let current = ensureState(input);
      return current.state.subscribe(callback);
    },
    use
  };
};
var allLoadersSettled = (cb) => {
  let check = () => {
    for (let states of allStates.values()) {
      for (let state of states.values()) {
        if (state.active) {
          setTimeout(check, 10);
          return;
        }
      }
    }
    cb();
  };
  check();
};

// src/resource/hooks.ts
import { useEffect as useEffect5, useLayoutEffect as useLayoutEffect3, useMemo as useMemo5, useRef as useRef4, useState as useState4 } from "react";

// src/useUnmount.ts
import { useEffect as useEffect4, useRef as useRef3 } from "react";
var useUnmount = (cb) => {
  let cbRef = useRef3(cb);
  cbRef.current = cb;
  let rendered = useRef3(true);
  rendered.current = true;
  useEffect4(() => {
    rendered.current = true;
    return () => {
      rendered.current = false;
      setTimeout(() => {
        if (!rendered.current)
          cbRef.current();
      }, 1e3);
    };
  }, []);
};

// src/resource/hooks.ts
var useListRetriever = (resource, name, ...args) => {
  let retriever = useMemo5(() => resource.retrievers(name), []);
  useUnmount(() => {
    retriever.dispose();
  });
  let [value, setValue] = useState4(() => retriever.initOptimistic(args));
  let [currentItems, setCurrentItems] = useState4(() => value.items);
  useLayoutEffect3(() => retriever.subscribe((value2) => {
    setValue(value2);
    if (value2.items.length || !value2.isLoading)
      setCurrentItems(value2.items);
  }), [retriever]);
  let paginationDataRef = useRef4(value.pagination);
  paginationDataRef.current = value.pagination;
  let [cursor, setCursor] = useState4(null);
  let prevArgs = useRef4(args);
  useEffect5(() => {
    if (args[0] === null) {
      setCursor(null);
      return;
    }
    if (args.length != prevArgs.current.length || !args.every((arg, i) => arg == prevArgs.current[i])) {
      setCursor(null);
      prevArgs.current = args;
    }
    let currentArgs = [...args];
    if (cursor)
      currentArgs[0] = {
        ...currentArgs[0],
        cursor
      };
    retriever.load({
      args: currentArgs,
      force: false
    });
  }, [retriever, cursor, ...args]);
  let loadNext = () => {
    if (!paginationDataRef.current?.afterCursor)
      return;
    setCursor(paginationDataRef.current.afterCursor);
  };
  let loadPrevious = () => {
    if (!paginationDataRef.current?.beforeCursor)
      return;
    setCursor(paginationDataRef.current.beforeCursor);
  };
  let refresh = () => {
    retriever.load({
      args,
      force: false
    });
  };
  let hasMoreAfter = !!value.pagination?.hasMoreAfter;
  let hasMoreBefore = !!value.pagination?.hasMoreBefore;
  return {
    ...value,
    data: currentItems,
    refresh,
    loadNext,
    loadPrevious,
    hasMoreAfter,
    hasMoreBefore
  };
};
var useItemRetriever = (resource, name, ...args) => {
  let listRetriever = useListRetriever(resource, name, ...args);
  return {
    ...listRetriever,
    items: void 0,
    data: listRetriever.items[0],
    notFound: listRetriever.error?.data.status == 404
  };
};

// src/resource/resourceActions.ts
import { canonicalize as canonicalize2 } from "@lb-lib/canonicalize";
import { ServiceError as ServiceError2, createError, isServiceError as isServiceError2 } from "@lb-lib/error";
import { Observer } from "@lb-lib/observer";

// src/resource/state.ts
var createResourceState = () => {
  let items = /* @__PURE__ */ new Map();
  let listeners = createStateEmitter();
  let setItems = (newItems) => {
    let updatedItems = /* @__PURE__ */ new Set();
    for (let item of newItems) {
      let existingItem = items.get(item.id);
      if (!existingItem || "updatedAt" in existingItem.item && "updatedAt" in item && existingItem.item.updatedAt <= item.updatedAt) {
        items.set(item.id, {
          item
        });
        updatedItems.add(item.id);
      }
    }
    return listeners.notify(Array.from(updatedItems));
  };
  let getItem = (id) => items.get(id)?.item;
  let getItems = (ids) => ids.map((id) => items.get(id)?.item).filter(Boolean);
  let createItemList = (initialItems) => {
    let listItemIds = new Set(initialItems.map((item) => item.id));
    let listListeners = createListeners();
    setItems(initialItems);
    let unregisterGlobalListener = {
      current: listeners.register(Array.from(listItemIds), listListeners.notify)
    };
    let setListItemIds = (newListItemIds) => {
      listItemIds.clear();
      for (let id of newListItemIds)
        listItemIds.add(id);
      unregisterGlobalListener.current();
      unregisterGlobalListener.current = listeners.register(Array.from(listItemIds), listListeners.notify);
    };
    let setListItems = (newItems) => {
      setListItemIds(newItems.map((item) => item.id));
      let {
        didNotify
      } = setItems(newItems);
      if (!didNotify) {
        listListeners.notify();
      }
    };
    let getListItems = () => getItems(Array.from(listItemIds));
    return {
      setItems: setListItems,
      getItems: getListItems,
      setItemIds: setListItemIds,
      register: listListeners.register,
      dispose: () => {
        unregisterGlobalListener.current();
        listListeners.dispose();
      }
    };
  };
  return {
    setItems,
    getItem,
    getItems,
    createItemList,
    register: listeners.register
  };
};

// src/resource/resourceActions.ts
var MIN_FETCH_INTERVAL = 1e3;
var normalizeItems = (items) => {
  if (Array.isArray(items))
    return {
      items
    };
  if ("items" in items)
    return {
      items: items.items,
      pagination: items.pagination
    };
  return {
    items: [items]
  };
};
var createResourceActions = (retrievers, actions, subActions) => {
  let itemState = createResourceState();
  let keyToLoaderPromise = /* @__PURE__ */ new Map();
  let keyToItemIdsCache = /* @__PURE__ */ new Map();
  let refetchCurrentRetrievers = createListeners();
  setInterval(() => {
    let now = Date.now();
    for (let [key, {
      ts
    }] of keyToLoaderPromise) {
      if (now - ts > MIN_FETCH_INTERVAL)
        keyToLoaderPromise.delete(key);
    }
  }, 60 * 1e3);
  let getCacheKey = (key, args) => {
    return `${key}:${canonicalize2(args)}`;
  };
  let hasCache = (cacheKey) => {
    return keyToItemIdsCache.has(cacheKey);
  };
  let createLoader2 = (cacheKey, retrieverKey, args, {
    force
  }) => {
    let current = keyToLoaderPromise.get(cacheKey);
    if (current && (!force && Date.now() - current.ts < MIN_FETCH_INTERVAL || current.active))
      return current.observer;
    let observer = new Observer();
    let cachedIds = keyToItemIdsCache.get(cacheKey);
    if (cachedIds) {
      setTimeout(() => {
        observer.notify({
          result: Promise.resolve({
            items: itemState.getItems(cachedIds)
          }),
          completed: false
        });
      }, 0);
    }
    let promise = retrievers[retrieverKey](...args).then((result) => {
      let normalized = normalizeItems(result);
      itemState.setItems(normalized.items);
      keyToItemIdsCache.set(cacheKey, normalized.items.map((item) => item.id));
      return normalized;
    }).catch((error) => {
      keyToItemIdsCache.delete(cacheKey);
      return Promise.reject(error);
    });
    promise.finally(() => {
      observer.notify({
        result: promise,
        completed: true
      });
      let val = keyToLoaderPromise.get(cacheKey);
      if (val) {
        keyToLoaderPromise.set(cacheKey, {
          ...val,
          active: false
        });
      }
    });
    keyToLoaderPromise.set(cacheKey, {
      ts: Date.now(),
      observer,
      active: true
    });
    return observer;
  };
  let getCurrentItemIDs = (cacheKey) => {
    return keyToItemIdsCache.get(cacheKey);
  };
  let createRetriever = (key) => {
    let retrieverState = new Observer();
    let itemList = itemState.createItemList([]);
    let currentLoaderId = 0;
    let unregister1 = refetchCurrentRetrievers.register(() => {
      if (retrieverState.lastValue?.input) {
        load({
          args: retrieverState.lastValue.input,
          force: true
        });
      }
    });
    let lastKey = "";
    let load = ({
      args,
      force
    }) => {
      let thisLoaderId = ++currentLoaderId;
      let cacheKey = getCacheKey(key, args);
      if (cacheKey != lastKey && !hasCache(cacheKey)) {
        lastKey = cacheKey;
        retrieverState.notify({
          input: args,
          isLoading: true
        });
      }
      let resultObserver = createLoader2(cacheKey, key, args, {
        force: !!force
      });
      let unsub = resultObserver.subscribeWithInitial(async ({
        result: resultPromise,
        completed
      }) => {
        if (completed)
          setTimeout(() => unsub(), 0);
        try {
          let {
            items,
            pagination
          } = await resultPromise;
          itemList.setItemIds(items.map((item) => item.id));
          if (thisLoaderId == currentLoaderId) {
            retrieverState.notify({
              input: args,
              isLoading: false,
              pagination
            });
          }
        } catch (error) {
          console.error(error);
          if (isServiceError2(error)) {
            retrieverState.notify({
              input: args,
              isLoading: false,
              error: isServiceError2(error) ? error : new ServiceError2(createError({
                status: -1,
                code: "failed_to_load",
                message: `An error occurred while loading this resource.`
              }))
            });
          }
        }
      });
    };
    let getValue = () => ({
      isLoading: retrieverState.lastValue?.isLoading ?? true,
      pagination: retrieverState.lastValue?.pagination,
      error: retrieverState.lastValue?.error,
      items: itemList.getItems()
    });
    let initOptimistic = (args) => {
      let cacheKey = getCacheKey(key, args);
      let cachedIds = getCurrentItemIDs(cacheKey);
      if (cachedIds) {
        itemList.setItemIds(cachedIds);
        retrieverState.notify({
          input: args,
          isLoading: false,
          pagination: void 0
        });
      }
      return getValue();
    };
    let subscribe = (callback) => {
      let cb = () => {
        callback(getValue());
      };
      let unsub1 = retrieverState.subscribe(cb);
      let unsub2 = itemList.register(cb);
      return () => {
        unsub1();
        unsub2();
      };
    };
    return {
      load,
      getValue,
      subscribe,
      initOptimistic,
      dispose: () => {
        unregister1();
        itemList.dispose();
        retrieverState.complete();
      },
      getPromise: () => new Promise((resolve, reject) => {
        let unsub = subscribe((value) => {
          if (value.error) {
            reject(value.error);
            setTimeout(() => unsub(), 0);
          } else if (!value.isLoading) {
            resolve(value.items);
            setTimeout(() => unsub(), 0);
          }
        });
      })
    };
  };
  let createAction = (key) => {
    let action = actions[key];
    return async (...args) => {
      try {
        let result = await action(...args);
        let items = (Array.isArray(result) ? result : [result]).filter(Boolean);
        itemState.setItems(items);
        return result;
      } finally {
        refetchCurrentRetrievers.notify();
      }
    };
  };
  let createSubAction = (key) => {
    let action = subActions[key];
    return async (...args) => {
      try {
        let result = await action(...args);
        return result;
      } finally {
        refetchCurrentRetrievers.notify();
      }
    };
  };
  return {
    createRetriever,
    createSubAction,
    createAction
  };
};

// src/resource/resource.ts
var Resource = class {
  constructor(name) {
    this.name = name;
  }
  #actions = {};
  #retrievers = {};
  #subActions = {};
  #resourceActions;
  addAction(name, fn) {
    this.#actions = {
      ...this.#actions,
      [name]: fn
    };
    return this;
  }
  addRetriever(name, fn) {
    this.#retrievers = {
      ...this.#retrievers,
      [name]: fn
    };
    return this;
  }
  addSubAction(name, fn) {
    this.#subActions = {
      ...this.#subActions,
      [name]: fn
    };
    return this;
  }
  build() {
    this.#resourceActions = createResourceActions(this.#retrievers, this.#actions, this.#subActions);
    return this;
  }
  get actions() {
    if (!this.#resourceActions)
      throw new Error("Resource not built");
    return this.#resourceActions.createAction;
  }
  get retrievers() {
    if (!this.#resourceActions)
      throw new Error("Resource not built");
    return this.#resourceActions.createRetriever;
  }
  get subActions() {
    if (!this.#resourceActions)
      throw new Error("Resource not built");
    return this.#resourceActions.createSubAction;
  }
};

// src/table/builder.ts
import { Observer as Observer4 } from "@lb-lib/observer";
import React7 from "react";

// src/table/components/index.tsx
import { memo as memo4 } from "@lb-lib/memo";
import { useObserver as useObserver2 } from "@lb-lib/observer";
import { Button as Button4, CenteredSpinner, Checkbox as Checkbox3, Error as Error4, Menu, Spacer as Spacer2, Text as Text2, theme as theme3, useIsGroup } from "@lb-lib/ui";
import { RiArrowDownSLine, RiMore2Line } from "@remixicon/react";
import { AnimatePresence, motion } from "framer-motion";
import React6, { Fragment, memo as reactMemo, useEffect as useEffect8, useMemo as useMemo8, useState as useState7 } from "react";
import { Link, useNavigate as useNavigate2 } from "react-router-dom";
import { styled as styled3 } from "styled-components";

// src/table/filter.ts
var getFilterPayload = (currentFilters) => {
  let payload = {};
  for (let filter of currentFilters) {
    let filterPayload = {};
    if (filter.type == "number" || filter.type == "date") {
      if (filter.operation == "eq")
        filterPayload = filter.value;
      else if (filter.operation == "gt")
        filterPayload = {
          gt: filter.value
        };
      else if (filter.operation == "lt")
        filterPayload = {
          lt: filter.value
        };
      else if (filter.operation == "gte")
        filterPayload = {
          gte: filter.value
        };
      else if (filter.operation == "lte")
        filterPayload = {
          lte: filter.value
        };
      else if (filter.operation == "between")
        filterPayload = {
          gte: filter.value[0],
          lte: filter.value[1]
        };
    } else if (filter.type == "string") {
      filterPayload = filter.value;
    } else if (filter.type == "select") {
      filterPayload = {
        in: filter.value
      };
    }
    for (let field of filter.fields) {
      payload[field] = filterPayload;
    }
  }
  for (let prop in payload) {
    if (!payload[prop])
      delete payload[prop];
  }
  return payload;
};
var serializeToQuery = (currentFilters) => {
  let query = new URLSearchParams();
  for (let filter of currentFilters) {
    if (filter.type == "number" || filter.type == "date") {
      if (filter.operation == "eq")
        query.append(filter.id, filter.value.toString());
      else if (filter.operation == "gt")
        query.append(`${filter.id}_gt`, filter.value.toString());
      else if (filter.operation == "lt")
        query.append(`${filter.id}_lt`, filter.value.toString());
      else if (filter.operation == "gte")
        query.append(`${filter.id}_gte`, filter.value.toString());
      else if (filter.operation == "lte")
        query.append(`${filter.id}_lte`, filter.value.toString());
      else if (filter.operation == "between") {
        query.append(`${filter.id}_gte`, filter.value[0].toString());
        query.append(`${filter.id}_lte`, filter.value[1].toString());
      }
    } else if (filter.type == "string") {
      query.append(filter.id, filter.value);
    } else if (filter.type == "select") {
      query.append(filter.id, filter.value.join(","));
    }
  }
  return query.toString();
};
var deserializeFromQuery = (query, filters) => {
  let filterState = [];
  for (let filter of filters) {
    let fields = filter.fields.map((f) => f.toString());
    let type = filter.type;
    if (type == "number") {
      let operation;
      let value;
      if (query.has(filter.id)) {
        operation = "eq";
        value = Number(query.get(filter.id));
      } else if (query.has(`${filter.id}_gt`)) {
        operation = "gt";
        value = Number(query.get(`${filter.id}_gt`));
      } else if (query.has(`${filter.id}_lt`)) {
        operation = "lt";
        value = Number(query.get(`${filter.id}_lt`));
      } else if (query.has(`${filter.id}_gte`) && query.has(`${filter.id}_lte`)) {
        operation = "between";
        value = [Number(query.get(`${filter.id}_gte`)), Number(query.get(`${filter.id}_lte`))];
      } else if (query.has(`${filter.id}_gte`)) {
        operation = "gte";
        value = Number(query.get(`${filter.id}_gte`));
      } else if (query.has(`${filter.id}_lte`)) {
        operation = "lte";
        value = Number(query.get(`${filter.id}_lte`));
      } else {
        continue;
      }
      filterState.push({
        id: filter.id,
        fields,
        type,
        operation,
        value
      });
    } else if (type == "date") {
      let operation;
      let value;
      if (query.has(filter.id)) {
        operation = "eq";
        value = new Date(query.get(filter.id));
      } else if (query.has(`${filter.id}_gt`)) {
        operation = "gt";
        value = new Date(query.get(`${filter.id}_gt`));
      } else if (query.has(`${filter.id}_lt`)) {
        operation = "lt";
        value = new Date(query.get(`${filter.id}_lt`));
      } else if (query.has(`${filter.id}_gte`) && query.has(`${filter.id}_lte`)) {
        operation = "between";
        value = [new Date(query.get(`${filter.id}_gte`)), new Date(query.get(`${filter.id}_lte`))];
      } else if (query.has(`${filter.id}_gte`)) {
        operation = "gte";
        value = new Date(query.get(`${filter.id}_gte`));
      } else if (query.has(`${filter.id}_lte`)) {
        operation = "lte";
        value = new Date(query.get(`${filter.id}_lte`));
      } else {
        continue;
      }
      filterState.push({
        id: filter.id,
        fields,
        type,
        operation,
        value
      });
    } else if (type == "string") {
      if (query.has(filter.id)) {
        filterState.push({
          id: filter.id,
          fields,
          type,
          operation: "eq",
          value: query.get(filter.id)
        });
      }
    } else if (type == "select") {
      if (query.has(filter.id)) {
        filterState.push({
          id: filter.id,
          fields,
          type,
          operation: "eq",
          value: query.get(filter.id).split(",")
        });
      }
    }
  }
  return filterState;
};

// src/table/components/columns.tsx
import { useObserver } from "@lb-lib/observer";
import { Button as Button2, SortableCheckList, Title, theme } from "@lb-lib/ui";
import * as RadixPopover from "@radix-ui/react-popover";
import { RiTableView } from "@remixicon/react";
import React4, { memo as memo2, useMemo as useMemo6, useState as useState5 } from "react";
import { keyframes, styled } from "styled-components";
var fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var fadeOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;
var Wrapper = styled("div").withConfig({
  componentId: "sc-o73vxf-0"
})`display:flex;align-items:center;flex-wrap:wrap;gap:10px;`;
var ColumnPopover = styled(RadixPopover.Content).withConfig({
  componentId: "sc-o73vxf-1"
})`background:${theme.colors.background};border:1px solid ${theme.colors.gray300};border-radius:10px;box-shadow:${theme.shadows.medium};z-index:9999;width:300px;max-height:400px;overflow-y:auto;&[data-state='open']{animation:${fadeIn} 0.15s ease-in-out;}&[data-state='closed']{animation:${fadeOut} 0.15s ease-in-out;}`;
var ColumnContent = styled("div").withConfig({
  componentId: "sc-o73vxf-2"
})`display:flex;flex-direction:column;`;
var ColumnContentHeader = styled("div").withConfig({
  componentId: "sc-o73vxf-3"
})`padding:14px 20px;border-bottom:1px solid ${theme.colors.gray300};position:sticky;top:0;background:${theme.colors.background};z-index:999;`;
var ColumnContentBody = styled("main").withConfig({
  componentId: "sc-o73vxf-4"
})`padding:15px 20px;flex-grow:1;`;
var Columns = memo2(({
  columns,
  layoutObserver
}) => {
  let layout = useObserver(layoutObserver) ?? [];
  let [open, setOpen] = useState5(false);
  let items = useMemo6(() => {
    let items2 = [];
    for (let c of layout) {
      let column = columns.find((column2) => column2.id == c.id);
      if (column) {
        items2.push({
          id: column.id,
          label: column.header,
          isChecked: c.isSelected
        });
      }
    }
    return items2;
  }, [columns, layout]);
  return /* @__PURE__ */ React4.createElement(Wrapper, null, /* @__PURE__ */ React4.createElement(RadixPopover.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React4.createElement(RadixPopover.Trigger, { asChild: true }, /* @__PURE__ */ React4.createElement(Button2, { iconLeft: /* @__PURE__ */ React4.createElement(RiTableView, null), size: "2", variant: "outline" }, "Columns")), /* @__PURE__ */ React4.createElement(RadixPopover.Portal, null, /* @__PURE__ */ React4.createElement(ColumnPopover, { sideOffset: 5 }, /* @__PURE__ */ React4.createElement(ColumnContent, null, /* @__PURE__ */ React4.createElement(ColumnContentHeader, null, /* @__PURE__ */ React4.createElement(Title, { as: "h1", size: "2", weight: "bold" }, "Customize Table")), /* @__PURE__ */ React4.createElement(ColumnContentBody, null, /* @__PURE__ */ React4.createElement(SortableCheckList, { items, onChange: (items2) => {
    layoutObserver.notify(items2.map(({
      id,
      isChecked
    }) => ({
      id,
      isSelected: isChecked
    })));
  } })))))));
});

// src/table/components/filter.tsx
import { Button as Button3, Checkbox as Checkbox2, DatePicker as DatePicker2, Input as Input2, Select as Select2, Spacer, Text, Title as Title2, Tooltip, theme as theme2 } from "@lb-lib/ui";
import * as RadixPopover2 from "@radix-ui/react-popover";
import { RiFilter2Line } from "@remixicon/react";
import React5, { memo as memo3, useEffect as useEffect6, useMemo as useMemo7, useRef as useRef5, useState as useState6 } from "react";
import { keyframes as keyframes2, styled as styled2 } from "styled-components";
var fadeIn2 = keyframes2`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var fadeOut2 = keyframes2`
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;
var Wrapper2 = styled2("div").withConfig({
  componentId: "sc-j5ouf1-0"
})`display:flex;align-items:center;flex-wrap:wrap;gap:10px;width:100%;`;
var CurrentFilters = styled2("div").withConfig({
  componentId: "sc-j5ouf1-1"
})`display:flex;gap:10px;align-items:center;flex-wrap:wrap;`;
var CurrentFilter = styled2("div").withConfig({
  componentId: "sc-j5ouf1-2"
})`flex-shrink:0;max-width:200px;height:22px;border:1px solid ${theme2.colors.gray300};border-radius:50px;display:flex;align-items:center;gap:5px;padding:0px 8px 0px 7px;cursor:pointer;user-select:none;`;
var FilterIcon = styled2("div").withConfig({
  componentId: "sc-j5ouf1-3"
})`color:${theme2.colors.gray600};svg{height:12;width:12;stroke-width:2.5;}`;
var FilterLabel = styled2("p").withConfig({
  componentId: "sc-j5ouf1-4"
})`color:${theme2.colors.gray800};font-weight:700;font-size:12px;flex-shrink:0;`;
var FilterValue = styled2("p").withConfig({
  componentId: "sc-j5ouf1-5"
})`color:${theme2.colors.gray700};font-size:12px;font-weight:500;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;`;
var FilterPopover = styled2(RadixPopover2.Content).withConfig({
  componentId: "sc-j5ouf1-6"
})`background:${theme2.colors.background};border:1px solid ${theme2.colors.gray300};border-radius:10px;box-shadow:${theme2.shadows.medium};overflow:hidden;z-index:9999;width:500px;height:350px;display:grid;grid-template-columns:160px auto;&[data-state='open']{animation:${fadeIn2} 0.15s ease-in-out;}&[data-state='closed']{animation:${fadeOut2} 0.15s ease-in-out;}`;
var FilterPopoverSide = styled2("div").withConfig({
  componentId: "sc-j5ouf1-7"
})`height:100%;overflow-y:auto;`;
var FilterSidebar = styled2(FilterPopoverSide).withConfig({
  componentId: "sc-j5ouf1-8"
})`background:${theme2.colors.gray100};border-right:1px solid ${theme2.colors.gray300};padding:20px;display:flex;flex-direction:column;gap:7px;`;
var FilterContent = styled2(FilterPopoverSide).withConfig({
  componentId: "sc-j5ouf1-9"
})`display:flex;flex-direction:column;form{display:flex;flex-direction:column;flex-grow:1;}`;
var FilterContentHeader = styled2("div").withConfig({
  componentId: "sc-j5ouf1-10"
})`padding:14px 20px;border-bottom:1px solid ${theme2.colors.gray300};position:sticky;top:0;background:${theme2.colors.background};z-index:999;`;
var FilterContentBody = styled2("main").withConfig({
  componentId: "sc-j5ouf1-11"
})`padding:20px;flex-grow:1;`;
var FilterContentFooter = styled2("footer").withConfig({
  componentId: "sc-j5ouf1-12"
})`padding:14px 20px;border-top:1px solid ${theme2.colors.gray300};position:sticky;bottom:0;background:${theme2.colors.background};z-index:999;display:flex;gap:10px;`;
var getFilterText = (filter, state) => {
  if (filter.type == "select") {
    return filter.options.filter((option) => state.value.includes(option.id)).map((option) => option.label).join(", ");
  }
  if (filter.type == "number" && Array.isArray(state.value)) {
    return `${state.value[0]} - ${state.value[1]}`;
  }
  if (filter.type == "date") {
    if (Array.isArray(state.value)) {
      return `${state.value[0].toLocaleDateString()} - ${state.value[1].toLocaleDateString()}`;
    }
    return state.value.toLocaleDateString();
  }
  return state.value.toString();
};
var TableFilters = memo3(({
  filters,
  filterState: [filterState, setFilterState]
}) => {
  let [open, setOpen] = useState6(false);
  let closedAtRef = useRef5(0);
  useEffect6(() => {
    if (!open)
      closedAtRef.current = Date.now();
  }, [open]);
  let [currentFilterId, setCurrentFilterId] = useState6(() => filters[0].id);
  let currentFilter = useMemo7(() => filters.find((f) => f.id == currentFilterId), [currentFilterId, filters]);
  let currentFilterState = useMemo7(() => filterState.find((f) => f.id == currentFilterId), [currentFilterId, filterState]);
  let applyFilter = (state) => {
    setFilterState((prev) => {
      if (state.value.length === 0) {
        return prev.filter((f) => f.id != state.id);
      }
      let index = prev.findIndex((f) => f.id == state.id);
      let next = [...prev];
      if (index == -1) {
        next.push(state);
      } else {
        next[index] = state;
      }
      return next;
    });
  };
  let resetFilter = (id) => {
    setFilterState((prev) => prev.filter((f) => f.id != id));
  };
  return /* @__PURE__ */ React5.createElement(Wrapper2, null, /* @__PURE__ */ React5.createElement(RadixPopover2.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React5.createElement(RadixPopover2.Trigger, { asChild: true }, /* @__PURE__ */ React5.createElement(Button3, { iconLeft: /* @__PURE__ */ React5.createElement(RiFilter2Line, null), size: "2" }, "Filter")), /* @__PURE__ */ React5.createElement(RadixPopover2.Portal, null, /* @__PURE__ */ React5.createElement(FilterPopover, { sideOffset: 5 }, /* @__PURE__ */ React5.createElement(FilterSidebar, null, filters.map((filter) => /* @__PURE__ */ React5.createElement(Button3, { variant: "ghost", onClick: () => setCurrentFilterId(filter.id), onHover: () => setCurrentFilterId(filter.id), color: filter.id == currentFilterId ? "blue" : void 0, size: "2", key: filter.id }, filter.label))), /* @__PURE__ */ React5.createElement(FilterContent, null, /* @__PURE__ */ React5.createElement(FilterContentHeader, null, /* @__PURE__ */ React5.createElement(Title2, { as: "h1", size: "2", weight: "bold" }, currentFilter?.description)), currentFilter?.type == "string" && /* @__PURE__ */ React5.createElement(FilterString, { filter: currentFilter, state: currentFilterState, apply: applyFilter, reset: currentFilterState ? () => resetFilter(currentFilterId) : void 0 }), currentFilter?.type == "select" && /* @__PURE__ */ React5.createElement(FilterSelect, { filter: currentFilter, state: currentFilterState, apply: applyFilter, reset: currentFilterState ? () => resetFilter(currentFilterId) : void 0 }), currentFilter?.type == "number" && /* @__PURE__ */ React5.createElement(FilterNumber, { filter: currentFilter, state: currentFilterState, apply: applyFilter, reset: currentFilterState ? () => resetFilter(currentFilterId) : void 0 }), currentFilter?.type == "date" && /* @__PURE__ */ React5.createElement(FilterDate, { filter: currentFilter, state: currentFilterState, apply: applyFilter, reset: currentFilterState ? () => resetFilter(currentFilterId) : void 0 }))))), /* @__PURE__ */ React5.createElement(CurrentFilters, null, filterState.map((state) => {
    let filter = filters.find((f) => f.id == state.id);
    if (!filter)
      return null;
    let text = getFilterText(filter, state);
    return /* @__PURE__ */ React5.createElement(Tooltip, { key: state.id, content: text }, /* @__PURE__ */ React5.createElement(CurrentFilter, { onClick: () => {
      if (state.id == currentFilterId && (open || Date.now() - closedAtRef.current < 200)) {
        setOpen(false);
        return;
      }
      setOpen(true);
      setCurrentFilterId(state.id);
      setTimeout(() => setCurrentFilterId(state.id), 20);
    } }, /* @__PURE__ */ React5.createElement(FilterIcon, null, /* @__PURE__ */ React5.createElement(RiFilter2Line, { size: 12 })), /* @__PURE__ */ React5.createElement(FilterLabel, null, filter.label), /* @__PURE__ */ React5.createElement(FilterValue, null, text)));
  }), filterState.length > 0 && /* @__PURE__ */ React5.createElement(Button3, { size: "1", variant: "ghost", onClick: () => setFilterState([]) }, "Clear all filters")));
});
var FilterString = ({
  filter,
  state,
  apply,
  reset
}) => {
  let [value, setValue] = useState6(() => state?.value ?? "");
  useEffect6(() => setValue(state?.value ?? ""), [state]);
  return /* @__PURE__ */ React5.createElement("form", { onSubmit: (e) => e.preventDefault() }, /* @__PURE__ */ React5.createElement(FilterContentBody, null, /* @__PURE__ */ React5.createElement(Input2, { placeholder: "Value", size: "2", label: filter.label, value, onChange: (e) => setValue(e.target.value) })), /* @__PURE__ */ React5.createElement(FilterContentFooter, null, /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", type: "submit", onClick: () => {
    apply({
      id: filter.id,
      fields: filter.fields,
      type: "string",
      operation: "eq",
      value
    });
  } }, "Apply"), /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", disabled: !reset, onClick: reset, type: "button" }, "Reset")));
};
var SelectList = styled2("ul").withConfig({
  componentId: "sc-j5ouf1-13"
})`list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:10px;`;
var SelectItem = styled2("li").withConfig({
  componentId: "sc-j5ouf1-14"
})``;
var FilterSelect = ({
  filter,
  state,
  apply,
  reset
}) => {
  let [value, setValue] = useState6(() => state?.value ?? []);
  useEffect6(() => setValue(state?.value ?? []), [state]);
  return /* @__PURE__ */ React5.createElement("form", { onSubmit: (e) => e.preventDefault() }, /* @__PURE__ */ React5.createElement(FilterContentBody, null, /* @__PURE__ */ React5.createElement(SelectList, null, filter.options.map((item) => /* @__PURE__ */ React5.createElement(SelectItem, { key: item.id }, /* @__PURE__ */ React5.createElement(Checkbox2, { label: item.label, checked: value.includes(item.id), onCheckedChange: (checked) => {
    setValue((prev) => {
      if (checked) {
        return [...prev, item.id];
      } else {
        return prev.filter((id) => id != item.id);
      }
    });
  } }))))), /* @__PURE__ */ React5.createElement(FilterContentFooter, null, /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", type: "submit", onClick: () => {
    apply({
      id: filter.id,
      fields: filter.fields,
      type: "select",
      operation: "eq",
      value
    });
  } }, "Apply"), /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", disabled: !reset, onClick: reset, type: "button" }, "Reset")));
};
var FilterNumber = ({
  filter,
  state,
  apply,
  reset
}) => {
  let [value1, setValue1] = useState6(() => (Array.isArray(state?.value) ? state.value[0] : state?.value) ?? "");
  let [value2, setValue2] = useState6(() => (Array.isArray(state?.value) ? state.value[1] : state?.value) ?? "");
  useEffect6(() => setValue1(Array.isArray(state?.value) ? state.value[0] : state?.value ?? ""), [state]);
  useEffect6(() => setValue2(Array.isArray(state?.value) ? state.value[1] : state?.value ?? ""), [state]);
  let [operation, setOperation] = useState6(() => state?.operation ?? "eq");
  let {
    valid,
    value1Num,
    value2Num
  } = useMemo7(() => {
    let value1Num2 = parseFloat(value1.toString());
    let value2Num2 = parseFloat(value2.toString());
    if (isNaN(value1Num2) || operation == "between" && isNaN(value2Num2)) {
      return {
        valid: false,
        value1Num: 0,
        value2Num: 0
      };
    }
    if (operation == "between" && value1Num2 > value2Num2) {
      return {
        valid: false,
        value1Num: 0,
        value2Num: 0
      };
    }
    return {
      valid: true,
      value1Num: value1Num2,
      value2Num: value2Num2
    };
  }, [value1, value2, operation]);
  return /* @__PURE__ */ React5.createElement("form", { onSubmit: (e) => e.preventDefault() }, /* @__PURE__ */ React5.createElement(FilterContentBody, null, /* @__PURE__ */ React5.createElement(Select2, { label: "Operation", value: operation, onChange: setOperation, size: "2", items: [{
    id: "eq",
    label: "Equals"
  }, {
    id: "gt",
    label: "Greater than"
  }, {
    id: "lt",
    label: "Less than"
  }, {
    id: "gte",
    label: "Greater than or equal"
  }, {
    id: "lte",
    label: "Less than or equal"
  }, {
    id: "between",
    label: "Between"
  }] }), /* @__PURE__ */ React5.createElement(Spacer, { size: 10 }), operation == "between" ? /* @__PURE__ */ React5.createElement("div", { style: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    width: "100%"
  } }, /* @__PURE__ */ React5.createElement(Input2, { placeholder: "Value", size: "2", label: "From", hideLabel: true, value: value1, type: "number", onChange: (e) => setValue1(e.target.value) }), /* @__PURE__ */ React5.createElement(Text, { as: "p", size: "1", weight: "medium" }, "and"), /* @__PURE__ */ React5.createElement(Input2, { placeholder: "Value", size: "2", label: "To", hideLabel: true, value: value2, type: "number", onChange: (e) => setValue2(e.target.value) })) : /* @__PURE__ */ React5.createElement(Input2, { placeholder: "Value", size: "2", label: "Value", hideLabel: true, value: value1, type: "number", onChange: (e) => setValue1(e.target.value) })), /* @__PURE__ */ React5.createElement(FilterContentFooter, null, /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", disabled: !valid, type: "submit", onClick: () => {
    apply({
      id: filter.id,
      fields: filter.fields,
      type: "number",
      operation,
      value: operation == "between" ? [value1Num, value2Num] : value1Num
    });
  } }, "Apply"), /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", disabled: !reset, onClick: reset, type: "button" }, "Reset")));
};
var FilterDate = ({
  filter,
  state,
  apply,
  reset
}) => {
  let [value1, setValue1] = useState6(() => Array.isArray(state?.value) ? state.value[0] : state?.value);
  let [value2, setValue2] = useState6(() => Array.isArray(state?.value) ? state.value[1] : state?.value);
  useEffect6(() => setValue1(Array.isArray(state?.value) ? state.value[0] : state?.value), [state]);
  useEffect6(() => setValue2(Array.isArray(state?.value) ? state.value[1] : state?.value), [state]);
  let [operation, setOperation] = useState6(() => state?.operation ?? "eq");
  let {
    valid
  } = useMemo7(() => {
    if (!value1 || operation == "between" && !value2 || operation == "between" && value1 > value2) {
      return {
        valid: false
      };
    }
    return {
      valid: true
    };
  }, [value1, value2, operation]);
  return /* @__PURE__ */ React5.createElement("form", { onSubmit: (e) => e.preventDefault() }, /* @__PURE__ */ React5.createElement(FilterContentBody, null, /* @__PURE__ */ React5.createElement(Select2, { label: "Operation", value: operation, onChange: setOperation, size: "2", items: [{
    id: "eq",
    label: "Equals"
  }, {
    id: "gt",
    label: "Greater than"
  }, {
    id: "lt",
    label: "Less than"
  }, {
    id: "gte",
    label: "Greater than or equal"
  }, {
    id: "lte",
    label: "Less than or equal"
  }, {
    id: "between",
    label: "Between"
  }] }), /* @__PURE__ */ React5.createElement(Spacer, { size: 10 }), operation == "between" ? /* @__PURE__ */ React5.createElement(DatePicker2, { placeholder: "Value", size: "2", label: "To", hideLabel: true, value: value1 && value2 ? [value1, value2] : void 0, type: "range", onChange: (val) => {
    setValue1(val[0]);
    setValue2(val[1]);
  } }) : /* @__PURE__ */ React5.createElement(DatePicker2, { placeholder: "Value", size: "2", label: "Value", hideLabel: true, value: value1, type: "single", onChange: (val) => setValue1(val) })), /* @__PURE__ */ React5.createElement(FilterContentFooter, null, /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", disabled: !valid, type: "submit", onClick: () => {
    apply({
      id: filter.id,
      fields: filter.fields,
      type: "date",
      operation,
      value: operation == "between" ? [value1, value2] : value1
    });
  } }, "Apply"), /* @__PURE__ */ React5.createElement(Button3, { variant: "soft", size: "2", disabled: !reset, onClick: reset, type: "button" }, "Reset")));
};

// src/table/components/query.ts
import { useEffect as useEffect7, useRef as useRef6 } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-use";
var useFilterQuery = ({
  filters,
  filterState: [filterState, setFilterState]
}) => {
  let location = useLocation();
  let navigate = useNavigate();
  let parsedQueryRef = useRef6(false);
  useEffect7(() => {
    if (parsedQueryRef.current)
      return;
    parsedQueryRef.current = true;
    try {
      let query = new URLSearchParams(location.search);
      let filterValues = deserializeFromQuery(query, filters || []);
      setFilterState(filterValues);
    } catch (e) {
      console.error(e);
    }
  }, []);
  let lastSetQueryRef = useRef6();
  useEffect7(() => {
    try {
      let query = serializeToQuery(filterState);
      if (lastSetQueryRef.current == query)
        return;
      lastSetQueryRef.current = query;
      navigate({
        search: query
      }, {
        preventScrollReset: true,
        replace: true
      });
    } catch (e) {
      console.error(e);
    }
  }, [filterState]);
};

// src/table/components/index.tsx
var Wrapper3 = styled3("div").withConfig({
  componentId: "sc-pagyty-0"
})`display:flex;flex-direction:column;width:100%;flex-grow:1;`;
var Header = styled3("header").withConfig({
  componentId: "sc-pagyty-1"
})`display:flex;justify-content:space-between;flex-wrap:wrap;align-items:center;margin-bottom:0px;`;
var HeaderSection = styled3("section").withConfig({
  componentId: "sc-pagyty-2"
})`display:flex;align-items:center;gap:10px;justify-content:space-between;width:100%;`;
var TableWrapper = styled3("div").withConfig({
  componentId: "sc-pagyty-3"
})`overflow-x:auto;width:100%;position:relative;flex-grow:1;`;
var Table = styled3("table").withConfig({
  componentId: "sc-pagyty-4"
})`width:100%;border-collapse:collapse;`;
var TableHead = styled3("thead").withConfig({
  componentId: "sc-pagyty-5"
})``;
var TableBody = styled3("tbody").withConfig({
  componentId: "sc-pagyty-6"
})``;
var TableRow = styled3("tr").withConfig({
  shouldForwardProp: (prop) => prop != "sidePadding"
}).withConfig({
  componentId: "sc-pagyty-7"
})`user-select:none;${(p) => typeof p.sidePadding == "number" ? `
    & > td,
    & > th {
      &:first-of-type {
        padding-left: ${p.sidePadding}px;
      }

      &:last-of-type {
        padding-right: ${p.sidePadding}px;
      }
    }
  ` : ""} &:not(:last-child){& > td,& > th{border-bottom:1px solid ${theme3.colors.gray300};}}`;
var TableCell = styled3("td").withConfig({
  componentId: "sc-pagyty-8"
})`padding:0;text-align:left;`;
var TableSelectCell = styled3(TableCell).withConfig({
  componentId: "sc-pagyty-9"
})`background:${theme3.colors.background};position:sticky;left:0;`;
var TableActionsCell = styled3(TableCell).withConfig({
  componentId: "sc-pagyty-10"
})`background:${theme3.colors.background};position:sticky;right:0;`;
var TableHeaderCell = styled3("th").withConfig({
  componentId: "sc-pagyty-11"
})`border-bottom:2px solid ${theme3.colors.gray300};padding:0;font-size:13px;font-weight:600;color:${theme3.colors.gray800};text-align:left;`;
var TableCellInner = styled3("div").withConfig({
  componentId: "sc-pagyty-12"
})`min-height:42px;padding:5px 4px;display:flex;align-items:center;white-space:nowrap;`;
var Footer = styled3("footer").withConfig({
  componentId: "sc-pagyty-13"
})`display:flex;justify-content:space-between;width:100%;margin-top:0px;align-items:center;`;
var ActionBarWrapper = styled3("div").withConfig({
  componentId: "sc-pagyty-14"
})`position:fixed;bottom:20px;left:0;right:0;display:flex;justify-content:center;pointer-events:none;`;
var ActionBar = styled3(motion.div).withConfig({
  componentId: "sc-pagyty-15"
})`background:${theme3.colors.foreground};border:1px solid ${theme3.colors.gray300};padding:12px 20px;border-radius:100px;max-width:500px;overflow-x:auto;box-shadow:${theme3.shadows.small};display:flex;gap:10px;align-items:center;pointer-events:auto;`;
var RowPanel = (props) => {
  return /* @__PURE__ */ React6.createElement(React6.Fragment, null, props.rowPanel(props.row));
};
var TableComponent = reactMemo((props) => {
  let filterState = useState7([]);
  let filterPayload = useMemo8(() => getFilterPayload(filterState[0]), [filterState[0]]);
  let isGroup = useIsGroup();
  let sidePadding = isGroup ? 10 : props.sidePadding;
  useFilterQuery({
    filters: props.filters,
    filterState
  });
  let state = props.state(props.props, {
    filter: filterPayload
  });
  let hookState = props.getHookState ? props.getHookState(state, props.props) : {};
  let loadingIds = hookState.loadingIds;
  let layout = useObserver2(props.layoutObserver);
  let currentColumns = useMemo8(() => layout ? layout.filter((c) => c.isSelected).map((c) => props.columns.find((col) => col.id == c.id)) : [], [layout, props.columns]);
  let [selectedItems, setSelectedItems] = useState7([]);
  let allSelected = state.items.length > 0 && selectedItems.length == state.items.length;
  useEffect8(() => {
    let items = selectedItems.filter((id) => state.items.find((i) => i.id == id));
    if (items.length != selectedItems.length)
      setSelectedItems(items);
  }, [state.items]);
  let hasBulkActions = props.bulkActions && props.bulkActions.length > 0;
  let hasRowActions = props.rowActions && props.rowActions.length > 0;
  let [initialLoading, setInitialLoading] = useState7(true);
  useEffect8(() => {
    if (!state.isLoading)
      setInitialLoading(false);
  }, [state.isLoading]);
  let link = useMemo8(() => props.link ? memo4(props.link) : void 0, [props.link]);
  let isFullLoading = initialLoading && state.isLoading;
  let navigate = useNavigate2();
  let hasHeader = props.filters && props.filters.length > 0 || props.customizable;
  let [openRowPanel, setOpenRowPanel] = useState7([]);
  if (state.items.length == 0 && filterState[0].length == 0 && !state.isLoading && !state.error && !state.hasMoreBefore && typeof props.emptyState == "function") {
    return /* @__PURE__ */ React6.createElement(Wrapper3, null, /* @__PURE__ */ React6.createElement(props.emptyState, null));
  }
  return /* @__PURE__ */ React6.createElement(Wrapper3, null, hasHeader && /* @__PURE__ */ React6.createElement(Header, { style: {
    paddingLeft: sidePadding,
    paddingRight: sidePadding,
    paddingTop: isGroup ? 10 : 0
  } }, /* @__PURE__ */ React6.createElement(HeaderSection, null, props.filters && props.filters.length > 0 && /* @__PURE__ */ React6.createElement(TableFilters, { filters: props.filters, filterState }), /* @__PURE__ */ React6.createElement(Spacer2, null), props.customizable && /* @__PURE__ */ React6.createElement(Columns, { columns: props.columns, layoutObserver: props.layoutObserver }))), isFullLoading && /* @__PURE__ */ React6.createElement("div", { style: {
    padding: "10px 0px"
  } }, /* @__PURE__ */ React6.createElement(CenteredSpinner, null)), state.error && /* @__PURE__ */ React6.createElement(Error4, null, state.error.data.message ?? state.error.message ?? "An error occurred"), !isFullLoading && !state.error && /* @__PURE__ */ React6.createElement(React6.Fragment, null, /* @__PURE__ */ React6.createElement(TableWrapper, { style: {
    opacity: state.isLoading ? 0.3 : 1,
    transition: "opacity 0.2s"
  } }, /* @__PURE__ */ React6.createElement(AnimatePresence, null, state.isLoading && /* @__PURE__ */ React6.createElement(motion.div, { initial: {
    opacity: 0
  }, animate: {
    opacity: 1
  }, exit: {
    opacity: 0
  }, transition: {
    duration: 0.2
  }, style: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1
  } }, /* @__PURE__ */ React6.createElement(motion.div, { initial: {
    scale: 0.5
  }, animate: {
    scale: 1
  }, transition: {
    duration: 0.2
  } }, /* @__PURE__ */ React6.createElement(CenteredSpinner, null)))), /* @__PURE__ */ React6.createElement(Table, null, /* @__PURE__ */ React6.createElement(TableHead, null, /* @__PURE__ */ React6.createElement(TableRow, { sidePadding }, props.rowPanel && /* @__PURE__ */ React6.createElement(TableHeaderCell, null), hasBulkActions && /* @__PURE__ */ React6.createElement(TableSelectCell, { as: "th", style: {
    width: "20px",
    borderBottom: `2px solid ${theme3.colors.gray300}`
  } }, /* @__PURE__ */ React6.createElement(TableCellInner, null, /* @__PURE__ */ React6.createElement(Checkbox3, { label: "Select all", hideLabel: true, checked: allSelected, onCheckedChange: (checked) => {
    if (checked) {
      setSelectedItems(state.items.map((i) => i.id));
    } else {
      setSelectedItems([]);
    }
  } }))), currentColumns.map((column) => /* @__PURE__ */ React6.createElement(TableHeaderCell, { key: column.id }, /* @__PURE__ */ React6.createElement(TableCellInner, null, column.header))), hasRowActions && /* @__PURE__ */ React6.createElement(TableHeaderCell, { style: {
    width: "26px"
  } }))), /* @__PURE__ */ React6.createElement(TableBody, null, state.items.map((row, i) => {
    let to = link?.(row, props.props);
    return /* @__PURE__ */ React6.createElement(Fragment, { key: `wrapper-${row.id}` }, /* @__PURE__ */ React6.createElement(TableRow, { sidePadding, key: row.id, onClick: (e) => {
      props.onItemClick?.(row);
      if (e.shiftKey) {
        let current = state.items.findIndex((i2) => i2.id == row.id);
        let last = state.items.findIndex((i2) => i2.id == selectedItems[0]);
        if (last == -1) {
          setSelectedItems([row.id]);
        } else {
          let start = Math.min(current, last);
          let end = Math.max(current, last);
          setSelectedItems(state.items.slice(start, end + 1).map((i2) => i2.id));
        }
      } else if (e.ctrlKey) {
        if (selectedItems.includes(row.id)) {
          setSelectedItems(selectedItems.filter((i2) => i2 != row.id));
        } else {
          setSelectedItems([...selectedItems, row.id]);
        }
      } else {
        if (props.rowPanel) {
          setOpenRowPanel((p) => {
            if (p.includes(row.id))
              return p.filter((id) => id != row.id);
            return [...p, row.id];
          });
        }
      }
    }, style: {
      background: selectedItems.includes(row.id) || props.selectedItemId == row.id ? theme3.colors.gray100 : "none",
      opacity: loadingIds?.includes(row.id) ? 0.5 : 1,
      transition: "opacity 0.2s"
    } }, props.rowPanel && /* @__PURE__ */ React6.createElement(TableCell, { style: {
      width: "16px",
      background: selectedItems.includes(row.id) ? theme3.colors.gray100 : theme3.colors.background,
      transition: "all .3s"
    } }, /* @__PURE__ */ React6.createElement(TableCellInner, null, /* @__PURE__ */ React6.createElement(RiArrowDownSLine, { size: 14, style: {
      color: theme3.colors.gray600
    } }))), hasBulkActions && /* @__PURE__ */ React6.createElement(TableSelectCell, { style: {
      width: "20px",
      background: selectedItems.includes(row.id) ? theme3.colors.gray100 : theme3.colors.background,
      transition: "all .3s"
    } }, /* @__PURE__ */ React6.createElement(TableCellInner, null, /* @__PURE__ */ React6.createElement(Checkbox3, { label: "Select row", hideLabel: true, checked: selectedItems.includes(row.id), onCheckedChange: (checked) => {
      if (checked) {
        setSelectedItems([...selectedItems, row.id]);
      } else {
        setSelectedItems(selectedItems.filter((i2) => i2 != row.id));
      }
    } }))), currentColumns.map((column) => /* @__PURE__ */ React6.createElement(TableCell, { key: column.id }, to ? /* @__PURE__ */ React6.createElement(Link, { to }, /* @__PURE__ */ React6.createElement(TableCellInner, null, column.render(row, props.props))) : /* @__PURE__ */ React6.createElement(TableCellInner, null, column.render(row, props.props)))), hasRowActions && /* @__PURE__ */ React6.createElement(TableActionsCell, { style: {
      width: "26px",
      background: selectedItems.includes(row.id) ? theme3.colors.gray100 : theme3.colors.background,
      transition: "all .3s"
    } }, /* @__PURE__ */ React6.createElement(TableCellInner, null, /* @__PURE__ */ React6.createElement(Menu, { items: [...props.rowActions.map((action) => ({
      id: action.id,
      label: action.label,
      disabled: action.disabled?.(row)
    })), ...to ? [{
      id: "$$open$$",
      label: "View"
    }] : []], onItemClick: (id) => {
      if (id == "$$open$$")
        return navigate(to);
      let rowAction = props.rowActions.find((a) => a.id == id);
      props.actions[rowAction.action]([row], hookState);
    } }, /* @__PURE__ */ React6.createElement(Button4, { size: "1", variant: "outline", iconLeft: /* @__PURE__ */ React6.createElement(RiMore2Line, null) }))))), props.rowPanel && openRowPanel.includes(row.id) && /* @__PURE__ */ React6.createElement(TableRow, { sidePadding }, /* @__PURE__ */ React6.createElement(TableCell, { colSpan: currentColumns.length + 1 + (hasRowActions ? 1 : 0) }, /* @__PURE__ */ React6.createElement(TableCellInner, null, /* @__PURE__ */ React6.createElement(RowPanel, { row, rowPanel: props.rowPanel })))));
  }), state.items.length == 0 && /* @__PURE__ */ React6.createElement(TableRow, { sidePadding }, /* @__PURE__ */ React6.createElement(TableCell, { colSpan: currentColumns.length + (hasBulkActions ? 1 : 0) + (hasRowActions ? 1 : 0) }, /* @__PURE__ */ React6.createElement(TableCellInner, { style: {
    padding: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  } }, /* @__PURE__ */ React6.createElement(Text2, { size: "1", color: "gray800", align: "center" }, state.hasMoreBefore ? "You have reached the end of this list" : typeof props.emptyState == "string" ? props.emptyState : "No items found matching your criteria"))))))), props.hasPagination && /* @__PURE__ */ React6.createElement(Footer, { style: {
    padding: `10px ${sidePadding ? sidePadding + 5 : 0}px`,
    borderTop: isGroup ? `1px solid ${theme3.colors.gray300}` : "none"
  } }, /* @__PURE__ */ React6.createElement(Text2, { size: "2", color: "gray700", weight: "medium" }, "Showing ", state.items.length, " item", state.items.length == 1 ? "" : "s"), /* @__PURE__ */ React6.createElement("div", { style: {
    display: "flex",
    gap: 5
  } }, /* @__PURE__ */ React6.createElement(Button4, { size: "1", variant: "outline", disabled: !state.hasMoreBefore, onClick: state.loadPrevious }, "Previous"), /* @__PURE__ */ React6.createElement(Button4, { size: "1", variant: "outline", disabled: !state.hasMoreAfter, onClick: state.loadNext }, "Next"))), /* @__PURE__ */ React6.createElement(ActionBarWrapper, null, /* @__PURE__ */ React6.createElement(AnimatePresence, null, selectedItems.length > 0 && /* @__PURE__ */ React6.createElement(ActionBar, { initial: {
    opacity: 0,
    scale: 0.5
  }, animate: {
    opacity: 1,
    scale: 1
  }, exit: {
    opacity: 0,
    scale: 0.5
  }, transition: {
    duration: 0.4,
    type: "spring",
    ease: "easeInOut",
    bounce: 0.35
  } }, /* @__PURE__ */ React6.createElement(Text2, { size: "1", color: "gray400", weight: "medium" }, /* @__PURE__ */ React6.createElement("strong", null, selectedItems.length), " items selected"), props.bulkActions.map((action) => /* @__PURE__ */ React6.createElement(Button4, { key: action.id, onClick: () => {
    let selected = state.items.filter((i) => selectedItems.includes(i.id));
    if (action.disabled) {
      selected = selected.filter((r) => !action.disabled(r));
    }
    if (selected.length == 0)
      return;
    props.actions[action.id](selected, hookState);
  }, size: "1" }, action.label)), /* @__PURE__ */ React6.createElement(Button4, { onClick: () => {
    setSelectedItems([]);
  }, size: "1" }, "Clear selection"))))));
});

// src/table/builder.ts
var Table2 = class {
  layoutObserver = new Observer4();
  opts;
  constructor(name, opts) {
    this.opts = {
      hasPagination: opts?.hasPagination !== false,
      customizable: opts?.customizable !== false,
      name,
      columns: [],
      state: () => {
        throw new Error("Not implemented");
      }
    };
  }
  columns(columns) {
    this.opts.columns.push(...columns);
    this.layoutObserver.notify(columns.map((c) => ({
      id: c.id,
      isSelected: c.isDefault
    })));
    return this;
  }
  link(link) {
    this.opts.link = link;
    return this;
  }
  state(state) {
    this.opts.state = state;
    return this;
  }
  hookState(getHookState) {
    this.opts.getHookState = getHookState;
    return this;
  }
  actions(actions) {
    this.opts.actions = {
      ...this.opts.actions,
      ...actions
    };
    return this;
  }
  rowPanel(rowPanel) {
    this.opts.rowPanel = rowPanel;
    return this;
  }
  rowActions(rowActions) {
    this.opts.rowActions = rowActions;
    return this;
  }
  bulkActions(bulkActions) {
    this.opts.bulkActions = bulkActions;
    return this;
  }
  filters(filters) {
    this.opts.filters = filters;
    return this;
  }
  search(placeholder) {
    this.opts.search = {
      placeholder
    };
    return this;
  }
  build() {
    let opts = {
      ...this.opts,
      layoutObserver: this.layoutObserver
    };
    return (props) => {
      return React7.createElement(TableComponent, {
        ...opts,
        sidePadding: props.sidePadding,
        emptyState: props.emptyState,
        props,
        onItemClick: props.onItemClick,
        selectedItemId: props.selectedItemId
      });
    };
  }
};

// src/useMutation.ts
import { ServiceError as ServiceError3, internalServerError, isServiceError as isServiceError3 } from "@lb-lib/error";
import { Error as Error5 } from "@lb-lib/ui";
import React8, { useEffect as useEffect9, useRef as useRef7, useState as useState8 } from "react";
import { toast } from "sonner";
var useMutation = (mutator, opts) => {
  let [loading, setLoading] = useState8(false);
  let [error, setError] = useState8(null);
  let [data, setData] = useState8(null);
  let [input, setInput] = useState8(null);
  let [success, setSuccess] = useState8(false);
  let mutate = async (input2) => {
    setLoading(true);
    setError(null);
    setInput(input2);
    try {
      let res = await mutator(input2);
      setData(res);
      setLoading(false);
      setSuccess(true);
      return res;
    } catch (err) {
      setLoading(false);
      let serviceError = isServiceError3(err) ? err : new ServiceError3(internalServerError({
        message: "An error occurred while processing your request. Please try again later."
      }));
      setError(serviceError);
      if (!opts?.disableToast)
        toast.error(serviceError.data.message);
    }
  };
  let successToRef = useRef7();
  useEffect9(() => {
    if (!success)
      return;
    successToRef.current = setTimeout(() => setSuccess(false), 2500);
    return () => clearTimeout(successToRef.current);
  }, [success]);
  let [successPermanent, setSuccessPermanent] = useState8(false);
  useEffect9(() => {
    if (success)
      setSuccessPermanent(true);
  }, [success]);
  let [loadingPermanent, setLoadingPermanent] = useState8(false);
  useEffect9(() => {
    if (loading)
      setLoadingPermanent(true);
  }, [loading]);
  return {
    mutate,
    isLoading: loading,
    isSuccess: success,
    isSuccessPermanent: successPermanent,
    isLoadingPermanent: loadingPermanent,
    error: error?.data,
    data,
    input,
    RenderError: () => {
      if (!error)
        return null;
      return React8.createElement(Error5, {
        style: {
          marginTop: 6
        },
        size: 12,
        children: error.data.message
      });
    }
  };
};
export {
  Resource,
  Table2 as Table,
  allLoadersSettled,
  atom,
  createListeners,
  createLoader,
  createStateEmitter,
  form,
  onFocus,
  useAtom,
  useForm,
  useFormComponent,
  useItemRetriever,
  useListRetriever,
  useMutation,
  useUnmount
};
//# sourceMappingURL=index.js.map